import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { combineLatest, map, Observable, of, pipe, switchMap } from 'rxjs';

import { Registration, RegistrationType } from 'app/features/manual-input-registration/models';
import { ContainerMeasurement } from 'app/features/manual-input-result-registration/models/container-measurement';
import { ScannedCode, ScannedCodeType } from 'app/features/scan-flow/models';
import {
    getActivityNumberFromScannedCodes,
    getBatchNumberFromScannedCodes,
    getScannedCodeNumbersByCodeType,
} from 'app/features/scan-flow/utils';
import { isResultRegistrationActivity } from 'app/features/scan-flow/utils/result-registration/container-measurement/scan-container-measurement.utils';
import { selectBatchByNumber } from 'app/shared/features/batch-selector/state/batch.selectors';
import { LocationType } from 'app/shared/features/location-selector/models';
import {
    createRowRegistrationFromRowSelection,
    RowRegistration,
} from 'app/shared/features/map-row-selector/models';
import { filterRowsSelectionByScannedCodes } from 'app/shared/features/map-row-selector/utils';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import { selectIsOnBreakIfLoaded } from 'app/state/break/break.selectors';

import { scanFlowFeatureKey, ScanFlowRegistrationData, ScanFlowState } from './scan-flow.reducer';
import {
    filterByCodeType,
    getAllowedCodeTypes,
    getScanFlowCodeDefinitionsForActivity,
    isScanFlowRegistrationDataComplete,
    isScanFlowRegistrationDataInProgress,
} from './scan-flow.utils';

export const selectScanFlowState = createFeatureSelector<ScanFlowState>(scanFlowFeatureKey);

export const selectScanFlowRegistrationData = createSelector(selectScanFlowState, (state) => state.data);

export const selectScanFlowInitializationProgress = createSelector(
    selectScanFlowState,
    (state) => state.initializationProgress,
);

export const selectScanFlowCodes = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.scannedCodes,
);

export const selectScanFlowAmountResult = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.amountResult,
);

export const selectScanFlowProductBoxRegistrations = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.productBoxRegistrations,
);
export const selectScanFlowRowSelection = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.rowSelection,
);

export const selectScanFlowWaitingForUserInput = createSelector(
    selectScanFlowState,
    (state) => state.waitingForUserInput,
);

export const selectScanFlowRowSelectionStatusByRowNumber = (rowNumber: number) =>
    createSelector(
        selectScanFlowRowSelection,
        (rowSelection) => rowSelection.find((row) => row.rowNumber === rowNumber)?.registrationStatus,
    );

export const selectScanFlowIsAutoRegistrationEnabled = (store: Observable<object>) =>
    combineLatest([
        store.pipe(select(selectScanFlowWaitingForUserInput)),
        store.pipe(selectIsOnBreakIfLoaded),
    ]).pipe(
        map(([waitingForUserInput, isOnBreak]) => {
            return !waitingForUserInput && !isOnBreak;
        }),
    );

export const selectScheduledRegistrationDate = createSelector(
    selectScanFlowState,
    (state) => state.scheduledRegistrationDate,
);

export const selectScanFlowCodesByCodeType = (codeType: ScannedCodeType) =>
    pipe(
        select(selectScanFlowCodes),
        map((scannedCodes) => filterByCodeType(scannedCodes, codeType)),
    );

export const selectScanFlowRegistrationDataCompleteness = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowRegistrationData),
        switchMap((registrationData) =>
            store.pipe(mapToScanFlowRegistrationDataCompleteness(registrationData)),
        ),
    );

// Check data completeness during the scan flow process
// It returns an observable that emits 'complete', 'in-progress', or 'empty'
// For registrationData, there's an additional check to see if the data is complete or in progress in
export const mapToScanFlowRegistrationDataCompleteness = (
    registrationData: ScanFlowRegistrationData | undefined,
) =>
    !registrationData
        ? () => of('empty')
        : pipe(
              mapScanFlowCodesToListDefinitions(registrationData.scannedCodes),
              map((scanCodeListDefinitions) => {
                  const isComplete = isScanFlowRegistrationDataComplete({
                      registrationData,
                      scanCodeListDefinitions,
                  });
                  if (isComplete) {
                      return 'complete';
                  }

                  const isInProgress = isScanFlowRegistrationDataInProgress({
                      registrationData,
                      scanCodeListDefinitions,
                  });
                  if (isInProgress) {
                      return 'in-progress';
                  }

                  return 'empty';
              }),
          );

export const selectScanFlowAllowedCodeTypes = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapToScanFlowAllowedCodeTypes(scannedCodes))),
    );

export const mapToScanFlowAllowedCodeTypes = (scannedCodes: ScannedCode[]) =>
    pipe(
        mapScanFlowCodesToListDefinitions(scannedCodes),
        map((scanFlowCodeListDefinitions) =>
            getAllowedCodeTypes({ scanFlowCodeListDefinitions, scannedCodes }),
        ),
    );

export const selectScanFlowActivity = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToActivity(scannedCodes))),
    );

export const selectScanFlowBatch = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToBatch(scannedCodes))),
    );

export const mapScanFlowCodesToBatch = (scannedCodes: ScannedCode[]) => (store: Observable<object>) => {
    const batchNumber = getBatchNumberFromScannedCodes(scannedCodes);
    return batchNumber ? store.pipe(selectBatchByNumber(batchNumber)) : of(undefined);
};

export const mapScanFlowCodesToActivity = (scannedCodes: ScannedCode[]) => (store: Observable<object>) => {
    const activityNumber = getActivityNumberFromScannedCodes(scannedCodes);
    return activityNumber ? store.pipe(selectActivityByNumber(activityNumber)) : of(undefined);
};

export const selectScanFlowCodeListDefinitions = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToListDefinitions(scannedCodes))),
    );

export const mapScanFlowCodesToListDefinitions = (scannedCodes: ScannedCode[]) =>
    pipe(
        mapScanFlowCodesToActivity(scannedCodes),
        map((activity) => getScanFlowCodeDefinitionsForActivity(activity)),
    );

export const mapScanFlowRegistrationDataToRowRegistrations = ({
    scannedCodes,
    rowSelection,
}: ScanFlowRegistrationData) => {
    const filteredRowSelection = filterRowsSelectionByScannedCodes({ scannedCodes, rowSelection });

    return filteredRowSelection.map(createRowRegistrationFromRowSelection);
};

export const mapScanFlowRegistrationDataToLocationsRegistrations = ({
    scannedCodes,
    rowSelection,
}: ScanFlowRegistrationData) => {
    const filteredRowSelection = filterRowsSelectionByScannedCodes({
        scannedCodes,
        rowSelection,
    });
    if (!filteredRowSelection || filteredRowSelection.length < 1) return undefined;
    return {
        locationNumber: filteredRowSelection[0].rowNumber,
        registrationStatus: 'done',
    };
};

export const transformScanFlowRegistrationData =
    ({
        registrationData,
        registrationTimestamp,
    }: {
        registrationData: ScanFlowRegistrationData;
        registrationTimestamp: number;
    }) =>
    (store: Observable<object>) =>
        combineLatest([
            store.pipe(mapScanFlowCodesToActivity(registrationData.scannedCodes)),
            store.pipe(mapScanFlowCodesToBatch(registrationData.scannedCodes)),
            store.pipe(mapToScanFlowRegistrationDataCompleteness(registrationData)),
        ]).pipe(
            map(([activity, batch, registrationDataCompleteness]) => {
                const isRegistrationDataComplete =
                    isResultRegistrationActivity(activity) && registrationDataCompleteness === 'empty'
                        ? 'complete'
                        : registrationDataCompleteness;

                if (!registrationData.employeeNumber || !activity || isRegistrationDataComplete === 'empty') {
                    return undefined;
                }

                // For activity with location type row, we set value of row
                // For activity with all other location type than row, we set value of location
                let rowRegistrations: RowRegistration[] = [];
                let locationRegistrations = undefined;
                if (activity.locationType === LocationType.Row)
                    rowRegistrations = mapScanFlowRegistrationDataToRowRegistrations(registrationData);
                else
                    locationRegistrations =
                        mapScanFlowRegistrationDataToLocationsRegistrations(registrationData);

                // For result registration activities, we only need the activity number, batch number, employee number, location and registration timestamp
                // Other fields are not available in the activity upon submission
                if (isResultRegistrationActivity(activity)) {
                    const registration: Registration = {
                        activityNumber: activity.number,
                        amountResult: undefined,
                        batchNumber: batch?.number ?? undefined,
                        containerNumbers: [],
                        employeeNumber: registrationData.employeeNumber,
                        productBoxRegistrations: [],
                        registrationType: RegistrationType.Labour,
                        rowRegistrations,
                        locationRegistrations,
                        registrationTimestamp,
                    };

                    return registration;
                }

                const registration: Registration = {
                    activityNumber: activity.number,
                    amountResult: registrationData.amountResult,
                    batchNumber: batch?.number ?? undefined,
                    containerNumbers: getScannedCodeNumbersByCodeType(
                        registrationData.scannedCodes,
                        'container',
                    ),
                    employeeNumber: registrationData.employeeNumber,
                    productBoxRegistrations: registrationData.productBoxRegistrations,
                    registrationType: RegistrationType.Labour,
                    rowRegistrations,
                    locationRegistrations,
                    registrationTimestamp,
                };

                return registration;
            }),
        );

export const transformScanFlowContainerMeasurementData =
    ({
        registrationData,
        registrationTimestamp,
    }: {
        registrationData: ScanFlowRegistrationData;
        registrationTimestamp: number;
    }) =>
    (store: Observable<object>) =>
        combineLatest([
            store.pipe(mapScanFlowCodesToActivity(registrationData.scannedCodes)),
            store.pipe(mapToScanFlowRegistrationDataCompleteness(registrationData)),
        ]).pipe(
            map(([activity, registrationDataCompleteness]) => {
                if (
                    !registrationData.employeeNumber ||
                    !activity ||
                    registrationDataCompleteness === 'empty'
                ) {
                    return undefined;
                }

                const containerMeasurement: ContainerMeasurement = {
                    employeeNumber: registrationData.employeeNumber,
                    activityNumber: activity.number,
                    amountResult: registrationData.amountResult,
                    containerNumber:
                        getScannedCodeNumbersByCodeType(registrationData.scannedCodes, 'container').at(0) ??
                        0,
                    registrationTimestamp,
                    productBoxRegistrations: registrationData.productBoxRegistrations,
                };

                return containerMeasurement;
            }),
        );

export const selectScanFlowAutoRegistrationAndCompleteness = (store: Observable<object>) =>
    combineLatest([
        store.pipe(selectScanFlowRegistrationDataCompleteness),
        store.pipe(selectScanFlowIsAutoRegistrationEnabled),
    ]).pipe(
        map(([registrationDataCompleteness, isAutoRegistrationEnabled]) => ({
            registrationDataCompleteness,
            isAutoRegistrationEnabled,
        })),
    );
